import debounce from 'debounce-promise';
import { getServiceConfig } from '@sitecore-search/data';
import Actions from '../actions.js';
import PageController from '../controller/page.js';
import AbortRequestError from '../dataAdapter/AbortRequestError.js';
import { cancelablePromise } from '../dataAdapter/helpers.js';
import DataAdapter from '../dataAdapter/index.js';
import Dispatcher from '../dispatcher.js';
import createRequest from './createRequest.js';
const DEFAULT_GROUP = 'default';
const groupByKey = ({ rfkId, entity }) => `${rfkId}@${entity}`;
const groupItemBy = (group, key, instanceId, widgetItem) => {
    if (!group.has(key)) {
        group.set(key, new Map([[instanceId, {}]]));
    }
    group.get(key).set(instanceId, widgetItem);
};
// Map of type Map is used to keep payload order
const groupRequests = (requests) => {
    const groupBy = new Map();
    // reverse requests to use only the latest ones and abort the previous ones.
    requests
        .slice()
        .reverse()
        .forEach(([requestCallPayload, options]) => {
        const { rfkId, instanceId, payload } = requestCallPayload;
        const key = groupByKey(requestCallPayload);
        if (groupBy.get(key)?.get(instanceId || '')) {
            options.abortController.abort();
        }
        else {
            groupItemBy(groupBy, key, instanceId || '', { ...payload, rfk_id: rfkId });
        }
    });
    return groupBy;
};
const handleWidgetRequest = (request, options) => {
    const { payload } = request;
    const searchRequest = createRequest();
    searchRequest.addWidgetItem(payload);
    if (request.context) {
        searchRequest.setContext(request.context.toJson());
    }
    return DataAdapter.fetch(searchRequest.toJson(), options);
};
const handleWidgetRequests = async (requests) => {
    const request = createRequest();
    // Groups requests by rfkId + instanceId
    // Note: for widgets all request, rfkId = null and instanceId = null
    const requestGroups = groupRequests(requests.filter(([_, options]) => !options.signal.aborted));
    // Collects all grouped requests: for every rfkId returns all its grouped instanceId requests
    // Example:
    //   request 1: rfkId = R1, instanceId = I1 and payload = P1
    //   request 2: rfkId = R2, instanceId = I2 and payload = P2
    //   request 3: rfkId = R1, instanceId = I3 and payload = P3
    //   request 4 (widgets all): rfkId = '', instanceId = undefined and payload = P4
    //   request 5: rfkId = R1, instanceId = I1 and payload = P5
    //
    //   widgetItems = [P5, P2, P3, P4]
    const widgetItems = Array.from(requestGroups.values())
        .map((item) => Array.from(item.values()))
        .flat();
    request.addWidgets(widgetItems);
    if (requests[0][0].context) {
        request.setContext(requests[0][0].context.toJson());
    }
    const { requestMiddleware } = getServiceConfig();
    // Only perform requestMiddleware if it is defined.
    requestMiddleware && (await requestMiddleware());
    const response = await DataAdapter.fetch(request.toJson());
    return requests.map(([, { abortController }]) => {
        if (abortController.signal.aborted) {
            return Object.assign({}, response, { _canceled: true });
        }
        return response;
    });
};
const WIDGET_REQUEST_DEBOUNCED_HANDLERS = {};
// @TODO: should unused handlers be destroyed after some time?
const getWidgetRequestDebouncedHandler = (by) => {
    if (!WIDGET_REQUEST_DEBOUNCED_HANDLERS[by]) {
        WIDGET_REQUEST_DEBOUNCED_HANDLERS[by] = debounce(handleWidgetRequests, 250, {
            accumulate: true,
        });
    }
    return WIDGET_REQUEST_DEBOUNCED_HANDLERS[by];
};
const widgetRequestDebouncedHandler = (widgetRequest, options) => {
    const handler = getWidgetRequestDebouncedHandler(options.groupBy);
    return handler(widgetRequest, options);
};
const getGroupBy = (request, options) => {
    if (options?.debounceBy) {
        return `debounceBy@${options.debounceBy}`;
    }
    const context = request.context?.toJson();
    if (context) {
        return JSON.stringify(context);
    }
    return DEFAULT_GROUP;
};
export default {
    async getWidgetData(widgetData, options) {
        const { rfkId, entity, request } = widgetData;
        const pageContext = PageController.getContext();
        const payload = request.toJson();
        Dispatcher.dispatch({
            type: Actions.WIDGET_DATA_REQUEST,
            payload: { ...widgetData, pageContext, payload },
        });
        try {
            const result = await this.fetchWidgetData({ ...widgetData, payload }, options);
            if (result._canceled) {
                Dispatcher.dispatch({
                    type: Actions.WIDGET_DATA_REQUEST_CANCELED,
                    payload: { ...widgetData, pageContext, payload },
                });
                return Promise.reject(new AbortRequestError(result));
            }
            const { _requestId: requestId, ...response } = result;
            const { widgets = [] } = response;
            const widgetItem = widgets.find(({ rfk_id: widgetId, entity: widgetEntity }) => widgetId === rfkId && widgetEntity === entity);
            if (!widgetItem) {
                throw new Error(`Response processing error: could not find ${rfkId} in response`);
            }
            Dispatcher.dispatch({
                type: Actions.WIDGET_DATA_REQUEST_SUCCESS,
                payload: { ...widgetData, pageContext, response: widgetItem, payload, requestId },
            });
            return widgetItem;
        }
        catch (error) {
            Dispatcher.dispatch({
                type: Actions.WIDGET_DATA_REQUEST_FAIL,
                payload: { error, ...widgetData, pageContext, payload },
            });
            return Promise.reject(error);
        }
    },
    fetchWidgetData(widgetRequest, options) {
        const abortController = new AbortController();
        const newOptions = {
            ...options,
            signal: abortController.signal,
            abortController,
            groupBy: getGroupBy(widgetRequest, options),
        };
        options?.signal?.addEventListener('abort', () => {
            abortController.abort();
        });
        const promise = options?.debounce
            ? widgetRequestDebouncedHandler(widgetRequest, newOptions)
            : handleWidgetRequest(widgetRequest, newOptions);
        return cancelablePromise(promise, abortController);
    },
};
